import React from 'react';
import { Bed, MapMarker } from '@withjoy/joykit/icons';
import { pxToRem } from '@withjoy/joykit/theme';
import Downshift from 'downshift';
import type { ChildrenFunction, ControllerStateAndHelpers } from 'downshift';
import { Container, Item, ValueContainer } from '@withjoy/joykit/components/SelectV1/styles';
import { Box, Flex, Popover, SelectV1, styled, TextV2 } from '@withjoy/joykit';
import { InputV2InlineLabel } from '@shared/joykit/packages/core/components/InputV2InlineLabel/InputV2InlineLabel';
import type { OptionsType, OptionType } from '@withjoy/joykit';
import { SELECT_SELECTED_OPTION_CLASS } from '@withjoy/joykit/components/SelectV1/constants';
import { Position } from '@shared/joykit/packages/core/common';
import { createLocationInputV2StateReducerHandler } from './createLocationInputV2StateReducerHandler';

const Menu = styled.ul<{ maxHeight: string; divideAt?: number }>`
  min-width: ${pxToRem(176)};
  overflow: auto;
  max-height: ${props => props.maxHeight};
  ${props =>
    props.divideAt &&
    `> li:nth-child(${props.divideAt}) {
            border-bottom: 1px solid #EBEBEB;
          }`};
`;

const RenderItem = styled(Item)`
  border-radius: ${pxToRem(8)};
  padding: ${props => props.theme.space[3]};
  :hover {
    cursor: pointer;
  }
  :first-of-type {
    border-radius: ${pxToRem(8)};
  }
  :last-of-type {
    border-radius: ${pxToRem(8)};
  }
`;

type LocationInputV2InlineLabelSelectV1Props<T extends OptionType> = {
  isHideListItemIcon?: boolean;
} & SelectV1<T>['props'];

type Props<T extends OptionType> = LocationInputV2InlineLabelSelectV1Props<T> &
  Readonly<{
    options: ReadonlyArray<T>;
    value?: T | null;
    noOptionsMessage?: string;
    loading?: boolean;
    loadingMessage?: (inputValue: string | null) => React.ReactNode;
    maxDropdownHeight?: string;
    afterMenuContent?: React.ReactNode;
    noBorder?: boolean;
    disabled?: boolean;
    label?: string;
  }>;

export class LocationInputV2InlineLabelSelectV1<T extends OptionType & { isHotel?: boolean }> extends SelectV1<T> {
  static defaultProps = {
    ...SelectV1.defaultProps,
    isHideListItemIcon: false,
  };

  props!: Props<T>;

  // Prevent Downshift v3 blur from clearing typed input when selectedItem is null in uncontrolled mode.
  protected stateReducerHandler = createLocationInputV2StateReducerHandler<T>(() => this.props);

  // Override the renderOptions method to add custom rendering logic
  protected renderOptions = (renderProps: ControllerStateAndHelpers<T>) => {
    const { selectedItem, highlightedIndex, getItemProps, inputValue } = renderProps;
    const { noOptionsMessage, loading, loadingMessage } = this.props;

    if (loading) {
      return <div>{typeof loadingMessage === 'function' ? loadingMessage(inputValue) : loadingMessage}</div>;
    }

    const filteredOptions = this.getOptions(renderProps);
    if (filteredOptions.length) {
      return (filteredOptions as OptionsType<T>).map((option, index) => {
        const isSelected = selectedItem === option;
        const isDisabled = this.isOptionDisabled(option);
        return (
          <RenderItem
            key={option.key || option.value}
            isHighlighted={highlightedIndex === index}
            isSelected={isSelected}
            isDisabled={isDisabled}
            {...getItemProps({
              key: option.key || option.value,
              item: option,
              index,
              ['aria-selected']: isSelected,
              disabled: isDisabled,
            })}
            className={isSelected && SELECT_SELECTED_OPTION_CLASS}
          >
            <Flex
              width="100%"
              columnGap={4}
              alignItems="center"
            >
              {!this.props.isHideListItemIcon && (option.isHotel ? <Bed size={32} /> : <MapMarker size={32} />)}
              <Flex flexDirection="column">
                <TextV2
                  typographyVariant="button2"
                  color="mono14"
                >
                  {option.label}
                </TextV2>
                <TextV2
                  typographyVariant="label2"
                  color="mono10"
                >
                  {option.subtitle}
                </TextV2>
              </Flex>
            </Flex>
          </RenderItem>
        );
      });
    }

    if (!inputValue?.trim()) return <></>;

    return (
      <Flex
        as="li"
        listStyle="none"
        minHeight={pxToRem(48)}
        justifyContent="center"
        padding={4}
      >
        {noOptionsMessage}
      </Flex>
    );
  };

  // Override the renderMenu method to use the new renderOptions method
  protected renderMenu = (renderProps: ControllerStateAndHelpers<T>) => {
    const { maxDropdownHeight, afterMenuContent = undefined } = this.props;
    const { innerRef, ...menuProps } = renderProps.getMenuProps({ refKey: 'innerRef' }, { suppressRefError: true });
    return (
      <Box
        ref={this.handleRefs.menuContainerRef}
        position="relative"
        borderRadius={pxToRem(12)}
      >
        <Menu
          {...menuProps}
          ref={innerRef}
          style={{ maxHeight: maxDropdownHeight }}
        >
          {this.renderOptions(renderProps)}
        </Menu>
        {afterMenuContent}
      </Box>
    );
  };

  // Override the render method to use the new render method
  public render() {
    const { defaultIsOpen, initialIsOpen, initialInputValue, defaultValue, disabled, fluid, searchable, inputValue, value, position, noBackground, zIndex } = this.props;
    return (
      <Downshift
        defaultIsOpen={defaultIsOpen}
        initialIsOpen={initialIsOpen}
        initialInputValue={initialInputValue}
        initialSelectedItem={defaultValue as unknown}
        inputValue={searchable ? inputValue : ''}
        isOpen={this.state.isOpen}
        itemToString={this.getItemDisplayValue}
        onSelect={this.handleOnChange}
        onInputValueChange={this.handleOnInputValueChange}
        onUserAction={this.handleDownshiftOnStateChange}
        stateReducer={this.stateReducerHandler}
        selectedItem={value as unknown}
      >
        {
          (renderProps => {
            return (
              <Container
                noBackground={noBackground}
                fluid={fluid}
                {...renderProps.getRootProps({
                  refKey: 'ref',
                })}
              >
                <Popover
                  disabled={disabled}
                  interactionVariant="click"
                  isOpen={renderProps.isOpen}
                  onInteraction={this.handlePopoverOnInteraction}
                  position={position || Position.BOTTOM_LEFT}
                  pinned={false}
                  boundary={'window'}
                  showArrow={false}
                  targetTagName="div"
                  usePortal={false}
                  wrapperTagName="div"
                  onEntering={this.handlePopoverOnEntering}
                  onEntered={this.handlePopoverOnEntered}
                  zIndex={zIndex}
                  contentProps={{
                    borderRadius: pxToRem(24),
                    paddingX: 6,
                    paddingTop: 7,
                    paddingBottom: 5,
                    overflowY: 'visible',
                    boxShadow: '0 3px 8px 0 rgba(0, 0, 0, 0.03), 0 10px 60px -20px rgba(44, 41, 37, 0.03), 0 9px 36px -30px rgba(0, 0, 0, 0.04)',
                  }}
                >
                  {this.renderValue(renderProps)}
                  {this.renderMenu(renderProps)}
                </Popover>
              </Container>
            );
          }) as ChildrenFunction<T>
        }
      </Downshift>
    );
  }

  // Override the renderValue method to use the new renderValue method
  protected renderValue = (renderProps: ControllerStateAndHelpers<T>) => {
    return <ValueContainer>{this.renderInput(renderProps)}</ValueContainer>;
  };

  // Override the renderInput method to use the new renderInput method
  protected renderInput = (renderProps: ControllerStateAndHelpers<T>) => {
    const { iconLeft, isInvalid, autoComplete, label, disabled } = this.props;
    return (
      <InputV2InlineLabel
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        {...renderProps.getInputProps({ disabled } as any)}
        ref={this.handleRefs.inputRef}
        label={label}
        aria-invalid={isInvalid}
        isInvalid={isInvalid}
        isDisabled={disabled}
        startElement={iconLeft}
        autoComplete={autoComplete}
      />
    );
  };
}
